import React, { useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import {
  HStack,
  VStack,
  Button,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Text,
  Box,
} from '@chakra-ui/react';
import styled from '../../../../consts/styled';
import { media, spacing } from '../../../../App/ChakraTheme';

import { AuthContext, Role } from '../../../../context/AuthProvider';
import NavigationSecondRow from '../../components/NavigationSecondRow';
import { Page } from '../../../../types';
import secondsToTime from '../../../../helpers/secondsToTime';
import { fullNameUsernameOrEmail } from '../../../../helpers/username';
import { DASHBOARD_STUDYGROUPS_AND_PINNIGS } from '../queries';
import { Query } from '../../../../generated/graphqlModels';
import VirtualClassrooms from './VirtualClassrooms';
import SelfLearning from './SelfLearning';
import Explore from './Explore';
import { useQueryParam } from '../../../../hooks/useQueryParams';
import { useUpdateQueryParams } from '../../../../helpers/updateQueryParams';
import Events from './Events';
import Challenge from './Challenge';
import SchoolFilter from '../Licenses/index/components/SchoolFilter';
import TeacherTraining from './TeacherTraining';
import { useTranslation } from 'react-i18next';
import { useIsMobile } from '../../../../hooks/isMobile';
import { ExternalLinkIcon } from '@chakra-ui/icons';
import getNewLabUrl from '../../../../helpers/getNewLabUrl';
import { StemiBeatLoader } from '../../../../components/Loader';
import FreePrograms from './FreePrograms';
import { ClassroomInfoCard } from '../Licenses/index/components/ClassroomInfoCard';
import { CreateClassroomModal } from '../Licenses/index/components/CreateClassroomModal';
import { useCanCreateClassroom } from '../../hooks/useCanCreateClassroom';

const GALLERY_URL = `${getNewLabUrl()}/gallery`;

const LeftColumn = styled(VStack)`
  width: 30%;
  flex: 1 0 70%;
  transition: flex-basis 400ms;
  ${media.tablet`
    width: 100%;
    flex: 1 0 100%;
  `};
`;

const updateQuery2 = (previousResult: any, { fetchMoreResult }: any) => {
  if (!fetchMoreResult) {
    return previousResult;
  }

  const previousEdges = previousResult.studyGroups;
  const fetchMoreEdges = fetchMoreResult.studyGroups;

  fetchMoreResult.studyGroups = [...previousEdges, ...fetchMoreEdges];

  return {
    ...fetchMoreResult,
    pinnings: previousResult.pinnings,
  };
};

const LOAD_INCREMENT = 15;

const DashboardPageNew: Page = styled(({ className }) => {
  const user = useContext(AuthContext);
  const location = useLocation();
  const [loadCount, setLoadCount] = useState<number>(LOAD_INCREMENT);
  const tabQuery = Number(useQueryParam('tab') || 0);
  const [tabIndex, setTabIndex] = useState(tabQuery);
  const [newExploreData, setNewExploreData] = useState(false);
  const [studyGroups, setStudyGroups] = useState<any[]>([]);
  const { t } = useTranslation();
  // This is hack to remove flicker on tabs change
  const [initialLoadFinished, setInitialLoadFinished] = useState(false);
  const { data, loading, fetchMore } = useQuery<Query>(
    DASHBOARD_STUDYGROUPS_AND_PINNIGS,
    {
      notifyOnNetworkStatusChange: true,
      variables: {
        userId: user?.pk,
        limit: loadCount,
        loadPinnings: true,
        customFilters: {
          isSelfLearning: false,
          isExplore: false,
          isChallenge: false,
          fieldsOrFilter: user?.flags.includes('only-related-classes')
            ? {
                fields: [
                  {
                    field: 'student_affiliations__user__id',
                    lookup: { iExact: user?.pk },
                  },
                  {
                    field: 'teacher_affiliations__user__id',
                    lookup: { iExact: user?.pk },
                  },
                ],
              }
            : undefined,
        },
      },
      // when signed out, user will be { is_authenticated: false }
      skip: user == null || user.pk == null,
    },
  );

  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const { canCreateClassroom } = useCanCreateClassroom();

  useEffect(() => {
    if (data?.studyGroups) {
      setStudyGroups(data?.studyGroups);
    }
  }, [data]);

  useEffect(() => {
    if (!loading && data) {
      if (!initialLoadFinished) {
        setInitialLoadFinished(true);
      }
    }
  }, [data, loading]);

  const updateQuery = useUpdateQueryParams();

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    queryParams.set('tab', tabIndex.toString());
    updateQuery(queryParams);
  }, [tabIndex]);

  useEffect(() => {
    if (tabIndex !== tabQuery) setTabIndex(tabQuery);
  }, [tabQuery]);

  const tabListRef = useRef<HTMLDivElement>(null);

  // Make all tabs reachable via Tab key (override Chakra's roving tabindex)
  useLayoutEffect(() => {
    const el = tabListRef.current;
    if (!el) return;
    const tabs = el.querySelectorAll<HTMLElement>('[role="tab"]');
    tabs.forEach(tab => { tab.tabIndex = 0; });
  });

  const loadAll = () => {
    fetchMore({
      variables: {
        limit: 5000,
        offset: loadCount,
        loadPinnings: false,
      },
      updateQuery: updateQuery2,
    }).then(() => {
      setLoadCount(loadCount + 5000);
    });
  };

  if (!user) {
    return null;
  }

  if (user.is_authenticated === false) {
    return <Navigate to="/sign-in/?next=/" />;
  }

  if (user.pending) {
    return (
      <div
        className={className}
        style={{
          display: 'flex',
          alignItems: 'center',
          flexDirection: 'column',
          marginTop: '50px',
          gap: '50px',
          textAlign: 'center',
        }}
      >
        <Text as="h3" textStyle="h3">
          {t('oldDashboardPage.accountPending')}
        </Text>

        <Text as="h5" textStyle="h5">
          {user.has_email
            ? t('oldDashboardPage.needToAcceptEmail')
            : t('oldDashboardPage.teacherNeedsToConfirm')}
        </Text>
        <Button
          colorScheme="blue"
          mt={8}
          onClick={() => window.location.reload()}
        >
          {t('general.refresh')}
        </Button>
      </div>
    );
  }

  const time_left = (user?.demo_end || 0) - new Date().getTime() / 1000;
  const hasTeacherTraining =
    user.roles.includes(Role.manager) ||
    user.roles.includes(Role.teacher) ||
    user.roles.includes(Role.staff);

  const hasLearnTab = !!(initialLoadFinished && data && data.studyGroups && (data.studyGroups.length > 0 || canCreateClassroom));
  const studentProjectsTabIndex = (hasLearnTab ? 1 : 0) + 3 + (hasTeacherTraining ? 1 : 0);

  const isMobile = useIsMobile();
  const tabFontSize = isMobile ? '12px' : '16px';

  return (
    <>
      <NavigationSecondRow>
        <Text as="h5" textStyle="h5">
          {t('greeting.welcome')},{' '}
          {fullNameUsernameOrEmail(user) || 'My Friend'}
          {time_left > 0 ? ` (${secondsToTime(time_left)} demo time left)` : ''}
        </Text>
      </NavigationSecondRow>
      {user?.pages?.study_group && (
        <div className="stemi-event-container" style={{ padding: '5px' }}>
          <Events />
        </div>
      )}
      {initialLoadFinished ?<div className={className}>
        <Tabs
          isLazy
          isManual
          mt={4}
          mb={8}
          index={tabIndex}
          onChange={(index) => {
            if (index !== studentProjectsTabIndex) {
              setTabIndex(index);
            }
          }}
          variant="solid-rounded"
        >
          <TabList ref={tabListRef} justifyContent={{ base: 'flex-start', md: 'center' }} mb={8} overflowX="auto" overflowY="hidden" pt={2} pb={2}
            sx={{ '.chakra-tabs__tab': { px: { base: 2, md: 4 }, py: { base: 1, md: 2 } } }}
          >
            {hasLearnTab &&
            <Tab marginLeft={3} fontSize={tabFontSize}>
              {t('tab.learn')}
            </Tab>}
            <Tab fontSize={tabFontSize}>
              {t('tab.homeLearn')}
            </Tab>
            <Tab fontSize={tabFontSize}>{t('tab.explore')}</Tab>
            <Tab fontSize={tabFontSize}>{t('tab.challenge')}</Tab>
            {hasTeacherTraining && (
              <Tab fontSize={tabFontSize}>{t('tab.training')}</Tab>
            )}
            <Tab
              fontSize={tabFontSize}
              aria-label={`${t('tab.studentProjects')} (opens in new tab)`}
              onClick={() => window.open(GALLERY_URL, '_blank', 'noopener,noreferrer')}
            >
              {t('tab.studentProjects')} <ExternalLinkIcon ml={1} mb="2px" />
            </Tab>
          </TabList>
          <TabPanels>
          {hasLearnTab &&<TabPanel>
              <HStack w="full" spacing={0} align="start" justify="start">
                <LeftColumn align="start">
                  {(user.roles.includes(Role.staff) ||
                    user.roles.includes(Role.manager) ||
                    user.roles.includes(Role.organization_user)) &&
                    time_left <= 0 &&
                    data?.studyGroups && (
                      <Box
                        w="full"
                        marginBottom={6}
                        textAlign="center"
                        onClick={() => {
                          if (loadCount < 100) {
                            loadAll();
                          }
                        }}
                      >
                        {loading && <StemiBeatLoader />}
                        <SchoolFilter
                          setFilteredSchools={setStudyGroups as any}
                          schools={(data?.studyGroups as any) || []}
                          isLoading={loading}
                        />
                      </Box>
                    )}
                  <VStack gap="4" align="start">
                    <VirtualClassrooms
                      user={user}
                      data={data ? { ...data, studyGroups } : data}
                      loading={loading}
                      loadCount={loadCount}
                      setLoadCount={setLoadCount}
                      // @ts-ignore
                      fetchMore={fetchMore}
                      LOAD_INCREMENT={LOAD_INCREMENT}
                    />
                    <ClassroomInfoCard
                      onCreateClassroom={() => setIsCreateModalOpen(true)}
                      canCreate={!!canCreateClassroom}
                    />
                    <CreateClassroomModal
                       isOpen={isCreateModalOpen}
                       onClose={() => setIsCreateModalOpen(false)}
                       onSuccess={() => {
                         window.location.reload();
                       }}
                    />
                    {data?.studyGroups && data.studyGroups.length > 0 && (
                      <SelfLearning user={user} />
                    )}
                  </VStack>
                </LeftColumn>
                {/* {user?.pages?.study_group && (
                  <RightColumn>
                    <Events />
                  </RightColumn>
                )} */}
              </HStack>
            </TabPanel>}
            <TabPanel>
              <FreePrograms />
            </TabPanel>
            <TabPanel>
              <Explore
                onNewBlog={(v) => {
                  setNewExploreData(v > 0);
                }}
              />
            </TabPanel>
            <TabPanel>
              <Challenge />
            </TabPanel>
            {hasTeacherTraining && (
              <TabPanel>
                <TeacherTraining />
              </TabPanel>
            )}
            <TabPanel />
          </TabPanels>
        </Tabs>
      </div> : null}
    </>
  );
})`
  padding-left: ${spacing.BASE_PLUS};
  padding-right: ${spacing.BASE_PLUS};

  > h3 {
    text-align: center;
  }

  > h1 {
    text-align: center;
    padding-top: ${spacing.BASE_PLUS};
    padding-bottom: ${spacing.BASE_PLUS};
  }

  > .row-cs {
    margin-right: -${spacing.TINY};
    margin-left: -${spacing.TINY};

    ${media.tablet`
    flex-wrap: wrap-reverse;
  `}
    > .column {
      padding-right: ${spacing.TINY};
      padding-left: ${spacing.TINY};
      padding-bottom: ${spacing.LARGE};

      > h3 {
        padding-bottom: 10px;
      }
    }
  }
` as any;

DashboardPageNew.getRoute = () => '/';

export default DashboardPageNew;
